import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI } from '../services/api';
import { Tag, Plus, Trash2, Check, X, Calendar, Users, Percent, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const DiscountCodes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  const [newCode, setNewCode] = useState({
    code: '',
    discount_percent: 10,
    max_uses: '',
    valid_until: '',
    valid_plans: [],
    first_time_only: true
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchCodes();
  }, [user]);

  const fetchCodes = async () => {
    try {
      const response = await billingAPI.getDiscountCodes();
      setCodes(response.data.discount_codes || []);
    } catch (error) {
      console.error('Failed to fetch codes:', error);
      toast.error('Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    
    if (!newCode.code.trim()) {
      toast.error('Please enter a code');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        code: newCode.code.toUpperCase(),
        discount_percent: parseInt(newCode.discount_percent),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        valid_until: newCode.valid_until || null,
        valid_plans: newCode.valid_plans.length > 0 ? newCode.valid_plans : null,
        first_time_only: newCode.first_time_only
      };
      
      await billingAPI.createDiscountCode(payload);
      toast.success('Discount code created!');
      setShowCreateModal(false);
      setNewCode({
        code: '',
        discount_percent: 10,
        max_uses: '',
        valid_until: '',
        valid_plans: [],
        first_time_only: true
      });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCode = async (codeId) => {
    setDeleting(codeId);
    try {
      await billingAPI.deleteDiscountCode(codeId);
      toast.success('Discount code deactivated');
      fetchCodes();
    } catch (error) {
      toast.error('Failed to delete code');
    } finally {
      setDeleting(null);
    }
  };

  const togglePlan = (planId) => {
    setNewCode(prev => ({
      ...prev,
      valid_plans: prev.valid_plans.includes(planId)
        ? prev.valid_plans.filter(p => p !== planId)
        : [...prev.valid_plans, planId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="discount-codes-page">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Admin
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Discount Codes
            </h1>
            <p className="text-muted-foreground mt-1">Manage promotional codes for subscriptions</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full font-semibold hover:bg-primary/90"
            data-testid="create-code-btn"
          >
            <Plus className="w-5 h-5" />
            Create Code
          </button>
        </div>

        {/* Codes List */}
        <div className="space-y-4">
          {codes.length === 0 ? (
            <div className="bg-card rounded-2xl p-12 border border-border text-center">
              <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No discount codes yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-2 bg-primary text-white rounded-full font-semibold hover:bg-primary/90"
              >
                Create First Code
              </button>
            </div>
          ) : (
            codes.map((code) => (
              <div
                key={code.id}
                className={`bg-card rounded-xl p-5 border ${code.active ? 'border-border' : 'border-destructive/30 bg-destructive/5'}`}
                data-testid={`code-item-${code.code}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${code.active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      <Percent className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-foreground">{code.code}</span>
                        {!code.active && (
                          <span className="px-2 py-0.5 bg-destructive/20 text-destructive text-xs rounded-full">Inactive</span>
                        )}
                        {code.first_time_only && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">First-time only</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Percent className="w-3 h-3" />
                          {code.discount_percent}% off
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {code.current_uses || 0}{code.max_uses ? `/${code.max_uses}` : ''} uses
                        </span>
                        {code.valid_until && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Until {new Date(code.valid_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {code.valid_plans && (
                        <div className="flex gap-1 mt-2">
                          {code.valid_plans.map(plan => (
                            <span key={plan} className="px-2 py-0.5 bg-muted text-xs rounded-full capitalize">{plan}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {code.active && (
                    <button
                      onClick={() => handleDeleteCode(code.id)}
                      disabled={deleting === code.id}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"
                      data-testid={`delete-code-${code.code}`}
                    >
                      {deleting === code.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-2xl p-6 max-w-md w-full border border-border">
              <h2 className="text-xl font-bold text-foreground mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Create Discount Code
              </h2>
              
              <form onSubmit={handleCreateCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Code</label>
                  <input
                    type="text"
                    value={newCode.code}
                    onChange={(e) => setNewCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., WELCOME20"
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
                    required
                    data-testid="new-code-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Discount Percentage</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCode.discount_percent}
                    onChange={(e) => setNewCode(prev => ({ ...prev, discount_percent: e.target.value }))}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max Uses (leave empty for unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode(prev => ({ ...prev, max_uses: e.target.value }))}
                    placeholder="Unlimited"
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Valid Until (optional)</label>
                  <input
                    type="date"
                    value={newCode.valid_until}
                    onChange={(e) => setNewCode(prev => ({ ...prev, valid_until: e.target.value }))}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Valid Plans (leave empty for all)</label>
                  <div className="flex flex-wrap gap-2">
                    {['weekly', 'monthly', 'quarterly', 'yearly'].map(plan => (
                      <button
                        key={plan}
                        type="button"
                        onClick={() => togglePlan(plan)}
                        className={`px-3 py-1 rounded-full text-sm capitalize ${
                          newCode.valid_plans.includes(plan)
                            ? 'bg-primary text-white'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {plan}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="first_time_only"
                    checked={newCode.first_time_only}
                    onChange={(e) => setNewCode(prev => ({ ...prev, first_time_only: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <label htmlFor="first_time_only" className="text-sm text-foreground">
                    First-time subscribers only
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-3 rounded-full bg-muted text-foreground font-semibold hover:bg-muted/80"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90 flex items-center justify-center gap-2"
                    data-testid="submit-code-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Code'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
